import { db } from '../firebase';
import {
    collection,
    addDoc,
    serverTimestamp,
    query,
    where,
    orderBy,
    limit,
    getDocs,
    updateDoc,
    doc,
    setDoc
} from 'firebase/firestore';
import { COLLECTION_NOTIFICATIONS, COLLECTION_COORDINATORS } from '../shared/constants';

/**
 * Send a notification to a specific user (Admin, Coordinator, or Doctor)
 * @param {string} userId - ID of the user to receive notification
 * @param {string} role - Role of the receiver
 * @param {Object} data - Notification content { title, message, referralId }
 */
export async function sendNotification(userId, role, data) {
    try {
        await addDoc(collection(db, COLLECTION_NOTIFICATIONS), {
            userId,
            role,
            ...data,
            read: false,
            createdAt: serverTimestamp()
        });
    } catch (error) {
        console.error('Error sending notification:', error);
    }
}

/**
 * Trigger notifications for referral status changes
 * @param {Object} referral - The referral object
 * @param {string} type - 'status_change' or 'new_referral'
 * @param {Object} extras - { senderId, senderName, newStatus }
 */
export async function triggerReferralNotifications(referral, type, extras) {
    // Cloud Functions (onReferralCreated, onReferralUpdated) now handle all 
    // internal and push notifications for referrals to ensure consistency and 
    // prevent duplicates. We can leave this as a placeholder or remove it.
    console.log(`🔔 Notification trigger received for type: ${type}. Cloud functions will process.`);
}

/**
 * Get notifications for a user
 * @param {string} userId 
 * @param {string} role 
 */
export async function getNotifications(userId, role) {
    try {
        const q = query(
            collection(db, COLLECTION_NOTIFICATIONS),
            where('userId', 'in', [userId, 'global_admin']), // Admins see global_admin ones too
            where('role', '==', role),
            orderBy('createdAt', 'desc'),
            limit(20)
        );
        const snapshot = await getDocs(q);
        return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
        console.error('Error getting notifications:', error);
        return [];
    }
}

/**
 * Mark notification as read
 */
export async function markAsRead(notificationId) {
    try {
        const docRef = doc(db, COLLECTION_NOTIFICATIONS, notificationId);
        await updateDoc(docRef, { read: true });
    } catch (error) {
        console.error('Error marking notification as read:', error);
    }
}
/**
 * Mark all notifications as read for a user
 */
export async function markAllAsRead(userId, role) {
    try {
        const q = query(
            collection(db, COLLECTION_NOTIFICATIONS),
            where('userId', 'in', [userId, 'global_admin']),
            where('role', '==', role),
            where('read', '==', false)
        );
        const snapshot = await getDocs(q);
        const updatePromises = snapshot.docs.map(d => updateDoc(d.ref, { read: true }));
        await Promise.all(updatePromises);
    } catch (error) {
        console.error('Error marking all as read:', error);
    }
}
